import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Service } from '../types/Service';
import AddLeadAssociationModal from './modals/AddLeadAssociationModal';
import { 
  PlusIcon, 
  TrashIcon, 
  XMarkIcon, 
  MagnifyingGlassIcon,
  AcademicCapIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';

interface LeadAssociationsTabProps {
  leadId: string;
  leadName: string;
  leadSegmentIds?: number[];
  onRefresh?: () => void;
}

interface UniversityAssociation {
  id: number;
  universityName: string;
  campusName?: string;
  courseName?: string;
  status: string;
  createdAt: string;
}

const LeadAssociationsTab: React.FC<LeadAssociationsTabProps> = ({ leadId, leadName, leadSegmentIds, onRefresh }) => {
  const [universities, setUniversities] = useState<UniversityAssociation[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [associationType, setAssociationType] = useState<'university' | 'service' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Service-related states
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');

  useEffect(() => {
    loadAssociations();
  }, [leadId]);

  const loadAssociations = async () => {
    setLoading(true);
    try {
      const [universitiesRes, servicesRes] = await Promise.all([
        api.get(`/lead-associations/lead/${leadId}?page=1&limit=100`).catch(err => ({ data: { data: [] } })),
        api.get(`/leads/${leadId}/services`).catch(err => ({ data: { data: [] } }))
      ]);

      const universities = universitiesRes.data?.data || [];
      const formattedUniversities = universities.map((assoc: any) => ({
        id: assoc.id,
        universityName: assoc.university_name || assoc.universityName,
        campusName: assoc.campus_name || assoc.campusName,
        courseName: assoc.course_name || assoc.courseName,
        status: assoc.is_active ? 'Active' : 'Inactive',
        createdAt: assoc.created_at || assoc.createdAt
      }));

      setUniversities(formattedUniversities);
      setServices(servicesRes.data?.data || []);
      setError(null);
    } catch (err: any) {
      console.error('Error loading associations:', err);
      setError(err.response?.data?.message || 'Failed to load associations');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableServices = async () => {
    try {
      let response;
      
      // If lead has segments, fetch only services available for those segments
      if (leadSegmentIds && leadSegmentIds.length > 0) {
        response = await api.get(`/services?segmentIds=${leadSegmentIds.join(',')}`);
      } else {
        // No segments, fetch all active services
        response = await api.get('/services');
      }
      
      const allSvcs = response.data.data || [];
      
      // Filter out already assigned
      const assignedIds = services.map(s => s.id);
      setAllServices(allSvcs.filter((s: Service) => !assignedIds.includes(s.id)));
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const handleOpenAddModal = (type: 'university' | 'service') => {
    setAssociationType(type);
    if (type === 'service') {
      loadAvailableServices();
    }
    setShowAddModal(true);
  };

  const handleAssignServices = async () => {
    if (selectedServiceIds.length === 0) return;

    try {
      await api.post(`/leads/${leadId}/assign-services`, { serviceIds: selectedServiceIds });
      setSuccessMessage(`${selectedServiceIds.length} service(s) assigned successfully`);
      setSelectedServiceIds([]);
      setShowAddModal(false);
      setAssociationType(null);
      await loadAssociations();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign services');
    }
  };

  const handleRemoveService = async (serviceId: number) => {
    if (!window.confirm('Remove this service?')) return;

    try {
      await api.delete(`/leads/${leadId}/services/${serviceId}`);
      setSuccessMessage('Service removed successfully');
      await loadAssociations();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove service');
    }
  };

  const handleRemoveUniversity = async (associationId: number) => {
    if (!window.confirm('Remove this university association?')) return;

    try {
      await api.delete(`/lead-associations/${associationId}`);
      setSuccessMessage('University association removed successfully');
      await loadAssociations();
      onRefresh?.();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove association');
    }
  };

  const filteredServices = allServices.filter(s =>
    s.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
    s.code.toLowerCase().includes(serviceSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading associations...</p>
      </div>
    );
  }

  const totalAssociations = universities.length + services.length;

  return (
    <div className="space-y-6">
      {/* Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Associations</h3>
            <p className="text-sm text-gray-500">
              {totalAssociations} total ({universities.length} universities, {services.length} services)
            </p>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Add Association</span>
            </button>
          </div>
        </div>

        {/* Universities Section */}
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
            <AcademicCapIcon className="h-5 w-5 mr-2 text-blue-600" />
            University Associations ({universities.length})
          </h4>
          {universities.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-sm text-gray-500">No university associations yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {universities.map((assoc) => (
                <div key={assoc.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{assoc.universityName}</p>
                    {assoc.campusName && <p className="text-sm text-gray-600">📍 {assoc.campusName}</p>}
                    {assoc.courseName && <p className="text-sm text-gray-600">📚 {assoc.courseName}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      <span className={`px-2 py-0.5 rounded-full ${assoc.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {assoc.status}
                      </span>
                      <span className="ml-2">{new Date(assoc.createdAt).toLocaleDateString()}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveUniversity(assoc.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Remove"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Services Section */}
        <div>
          <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
            <BriefcaseIcon className="h-5 w-5 mr-2 text-purple-600" />
            Services ({services.length})
          </h4>
          {services.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-sm text-gray-500">No services assigned yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {services.map((service) => (
                <div key={service.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{service.name}</p>
                    <p className="text-sm text-gray-600">{service.description || 'No description'}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{service.code}</span>
                      <span className="ml-2">{new Date((service as any).assigned_at || service.createdAt).toLocaleDateString()}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveService(service.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Remove"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Association Modal */}
      {showAddModal && !associationType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Add Association</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6">Choose the type of association to add:</p>

            <div className="space-y-3">
              <button
                onClick={() => handleOpenAddModal('university')}
                className="w-full p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <AcademicCapIcon className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">University Association</p>
                    <p className="text-sm text-gray-600">Link to university, campus, and course</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleOpenAddModal('service')}
                className="w-full p-4 border-2 border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <BriefcaseIcon className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Service Assignment</p>
                    <p className="text-sm text-gray-600">Assign services to this lead</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* University Association Modal */}
      {showAddModal && associationType === 'university' && (
        <AddLeadAssociationModal
          isOpen={true}
          onClose={() => {
            setShowAddModal(false);
            setAssociationType(null);
          }}
          onSuccess={() => {
            loadAssociations();
            onRefresh?.();
            setSuccessMessage('University association added successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
          }}
          leadId={leadId}
          leadName={leadName}
        />
      )}

      {/* Service Assignment Modal */}
      {showAddModal && associationType === 'service' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Assign Services to {leadName}</h2>
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setAssociationType(null);
                  setSelectedServiceIds([]);
                  setServiceSearchTerm('');
                }} 
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={serviceSearchTerm}
                    onChange={(e) => setServiceSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Services List */}
              <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                {filteredServices.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {allServices.length === 0 ? 'All services are already assigned' : 'No services found'}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredServices.map((service) => (
                      <label
                        key={service.id}
                        className="flex items-center p-4 hover:bg-purple-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedServiceIds.includes(service.id)}
                          onChange={() => {
                            setSelectedServiceIds(prev =>
                              prev.includes(service.id)
                                ? prev.filter(id => id !== service.id)
                                : [...prev, service.id]
                            );
                          }}
                          className="h-4 w-4 text-purple-600 rounded"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{service.name}</p>
                              <p className="text-xs text-gray-500">{service.description || 'No description'}</p>
                            </div>
                            <span className="px-2 py-1 text-xs font-mono bg-gray-100 text-gray-700 rounded">
                              {service.code}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {selectedServiceIds.length > 0 && (
                <div className="mt-3 px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-800">
                    {selectedServiceIds.length} service(s) selected
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAssociationType(null);
                  setSelectedServiceIds([]);
                  setServiceSearchTerm('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignServices}
                disabled={selectedServiceIds.length === 0}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                Assign {selectedServiceIds.length > 0 && `(${selectedServiceIds.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadAssociationsTab;
